import { lazy, Suspense } from "react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { BestSellers } from "@/components/BestSellers";
import { Footer } from "@/components/Footer";
import { SEOHead, createHomepageSchema, createFAQSchema } from "@/components/SEOHead";

import { FAQ } from "@/components/FAQ";
import { HomepageAuthorityHub } from "@/components/HomepageAuthorityHub";
import { TrustBarLogos } from "@/components/TrustBarLogos";

import { ThcaVsThcSection } from "@/components/ThcaVsThcSection";
import { HomepageTopicalAuthority } from "@/components/HomepageTopicalAuthority";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

// Lazy load non-critical components
const NewProductsSection = lazy(() =>
  import("@/components/NewProductsSection").then((m) => ({ default: m.NewProductsSection })),
);
const AllProductsPreview = lazy(() =>
  import("@/components/AllProductsPreview").then((m) => ({ default: m.AllProductsPreview })),
);

const InternalLinks = lazy(() => import("@/components/InternalLinks").then((m) => ({ default: m.InternalLinks })));

// Minimal loading fallback
const SectionFallback = () => <div className="py-12" />;

// Homepage structured data for Google Rich Results
const homepageSchema = createHomepageSchema();

// FAQ Schema - matches visible FAQ content exactly
const homepageFAQs = [
  {
    question: "Är THCA lagligt i Sverige 2026?",
    answer: "Ja, THCA är inte narkotikaklassat i Sverige per 2026. Till skillnad från THC finns THCA inte med på Läkemedelsverkets förteckning över kontrollerade substanser. THCA är den naturliga syraformen av THC som förekommer i hampaväxter och är i sig icke-psykoaktiv. Alla våra produkter uppfyller gällande svensk lagstiftning och är labbtestade av oberoende laboratorier.",
  },
  {
    question: "Hur snabbt levererar ni THCA vapes i Sverige?",
    answer: "Beställningar som läggs före kl 14:00 på vardagar skickas samma dag med PostNord. Normal leveranstid är 1–3 vardagar till hela Sverige. Alla paket skickas diskret utan synlig produktinformation och du får ett spårningsnummer via e-post direkt när paketet lämnar vårt lager. Fri frakt gäller på alla beställningar över 499 kr.",
  },
  {
    question: "Hur stark är en Magic Sauce THCA vape?",
    answer: "Magic Sauce är vår starkaste THCA vape med 99 % renhet och 2 ml kapacitet, vilket ger cirka 1 000 puffar per enhet. Det gör den till en av de mest potenta THCA-produkterna på den svenska marknaden. För dig som är ny till THCA rekommenderar vi Snozy-serien med cirka 80 % renhet.",
  },
  {
    question: "Vilka betalningsmetoder accepterar ni?",
    answer: "Vi accepterar Visa, Mastercard och Revolut för säkra och smidiga betalningar. Alla transaktioner sker via krypterade anslutningar för att skydda dina uppgifter. Vi erbjuder även paketpriser där du kan spara upp till 25 % på utvalda produkter.",
  },
];
const faqSchema = createFAQSchema(homepageFAQs);

// Default SEO values
const DEFAULT_SEO = {
  title: "THCA Sverige – Köp THCA Vape & Magic Sauce",
  description:
    "THCA Sverige: handla labbtestade THCA vapes och Magic Sauce vape lagligt online. Fri frakt över 499 kr, diskret leverans 1–3 vardagar.",
};

/**
 * Homepage with 11 sections:
 * 1. Hero (H1: THCA Sverige – Köp THCA Vape och Magic Sauce Lagligt)
 * 2. BestSellers (THCA Vape Sverige)
 * 3. HomepageAuthorityHub (Product overview)
 * 4. NewProductsSection (Nyheter)
 * 5. AllProductsPreview (THCA Buds & Hash Sverige)
 * 6. Categories
 * 7. TrustBarLogos (Real brand logos)
 * 8. HomepageSEOHub (NEW - 900+ word authority content)
 * 9. FAQ
 * 10. InternalLinks
 */
const Index = () => {
  useRevealOnScroll();

  const seoTitle = DEFAULT_SEO.title;
  const seoDescription = DEFAULT_SEO.description;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords="thca sverige, thca vape, magic sauce vape, köp thca, thca vape sverige, laglig cannabis sverige, vapebolag"
        schema={[...homepageSchema, faqSchema]}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="space-y-0">
          {/* 1. Hero */}
          <Hero />

          {/* 2. Trust Bar */}
          <TrustBarLogos />

          {/* 3. BestSellers */}
          <div className="reveal-section">
            <BestSellers />
          </div>

          {/* 4. New Products */}
          <div className="reveal-section">
            <Suspense fallback={<SectionFallback />}>
              <NewProductsSection />
            </Suspense>
          </div>

          {/* 5. THCA Buds & Hash */}
          <div className="reveal-section">
            <Suspense fallback={<SectionFallback />}>
              <AllProductsPreview />
            </Suspense>
          </div>

          {/* 6. Authority Hub */}
          <div className="reveal-section">
            <HomepageAuthorityHub />
          </div>

          {/* 7. THCA vs THC */}
          <div className="reveal-section">
            <ThcaVsThcSection />
          </div>

          {/* 8. Topical Authority SEO Hub */}
          <div className="reveal-section">
            <HomepageTopicalAuthority />
          </div>

          {/* 9. FAQ */}
          <div className="reveal-section">
            <FAQ />
          </div>

          {/* 9. Internal Links */}
          <div className="reveal-section">
            <Suspense fallback={<SectionFallback />}>
              <InternalLinks variant="homepage" />
            </Suspense>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Index;
